import { Component, Input, OnInit } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { AbstractModalWithResult } from 'src/app/shared/components/abstract-modal-with-result';
import { TagGroup, TaskState } from 'src/app/shared/models/misc.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { ModalDismissRoleEnum } from 'src/app/task-process/answer-editor/send-and-popover/modal-dismiss-role.enum';
import { AfterSendAction, AfterSendActionType } from '../../shared/model';
import { TaskProcessContext } from '../../shared/task-process-context.class';
import { SendAndAskForHelpComponent } from './send-and-ask-for-help/send-and-ask-for-help.component';
import { SendAndTeamSelectionComponent } from './send-and-team-selection/send-and-team-selection.component';
import { TeamOption } from './send-and-team-selection/team-option.model';

@Component({
  selector: 'app-send-and-popover',
  templateUrl: './send-and-popover.component.html',
  styleUrls: ['../../../shared/components/select-in-popover/select-in-popover.component.scss'],
})
export class SendAndPopoverComponent extends AbstractModalWithResult<AfterSendAction> implements OnInit {
  static modalId = 'SendAndPopoverComponent';

  @Input() taskProcessContext: TaskProcessContext;

  showSurveyButton: boolean;
  routingTagGroup: TagGroup;
  tagOptions: TeamOption[];
  hasAtLeastOneOtherTeam: boolean;
  canAnswerAndAskForHelp: boolean;

  constructor(popoverController: PopoverController, private basicService: BasicService) {
    super(popoverController);
  }

  getModalId() {
    return SendAndPopoverComponent.modalId;
  }

  ngOnInit() {
    this.showSurveyButton = this.taskProcessContext.shouldOfferToTriggerSurvey();
    this.routingTagGroup = this.taskProcessContext.tagsContext.getRoutingTagGroup();
    this.tagOptions = this.routingTagGroup.tags
      .filter(tagValue => !tagValue.isCategory && tagValue.id !== this.routingTagGroup.selectedTagId)
      .filter(tagValue => this.taskProcessContext.tagsContext.isTagVisible(tagValue))
      .map(tagValue => ({
        tagId: tagValue.id,
        label: tagValue.name,
      }));
    this.hasAtLeastOneOtherTeam = this.tagOptions.length >= 1;

    this.determineIfUserCanAnswerAndAskForHelp();
  }

  sendAndPutOnHold() {
    super.ok({ type: AfterSendActionType.PutOnHold });
  }

  sendAndTriggerSurvey() {
    super.ok({ type: AfterSendActionType.TriggerSurvey });
  }

  async selectTeam() {
    const componentProps = {
      teams: this.tagOptions,
    };
    const modalResult = await this.basicService.popoverWithResult<TeamOption>(
      null,
      SendAndTeamSelectionComponent,
      componentProps
    );
    if (modalResult.role === ModalDismissRoleEnum.OK) {
      this.onTeamSelected(modalResult.data.tagId);
    }
  }

  private onTeamSelected(newTeamId) {
    const currentTeam = this.routingTagGroup.tags.find(tag => tag.id === this.routingTagGroup.selectedTagId);

    // compute which tag groups, if any, should be reset to 'None'
    // because their current value depends upon the tag value that is changing
    const groupsToReset: TagGroup[] =
      this.taskProcessContext.tagsContext.getTagGroupsWhoseValueDependUponTagValue(currentTeam);

    // Build list of tags that should be set after /execture-answer succeeds
    const tagIds = [newTeamId, ...groupsToReset.map(tg => tg.noneTagId)];
    super.ok({
      type: AfterSendActionType.Route,
      details: { tagIds },
    });
  }

  async askForHelp() {
    const modalResult = await this.basicService.popoverWithResult<any>(null, SendAndAskForHelpComponent);
    if (modalResult.role === ModalDismissRoleEnum.OK) {
      this.onHelpSelected(modalResult.data);
    }
  }

  private onHelpSelected(message: string) {
    super.ok({
      type: AfterSendActionType.AskForHelp,
      details: { message },
    });
  }

  private determineIfUserCanAnswerAndAskForHelp(): void {
    if (this.taskProcessContext?.processedTaskInfo?.state) {
      const needsHelp = (taskState: TaskState) => [TaskState.HelpAnswered, TaskState.HelpRequested].includes(taskState);
      const { taskStateCode, previousStateCode } = this.taskProcessContext.processedTaskInfo.state;
      this.canAnswerAndAskForHelp = !needsHelp(taskStateCode) && !needsHelp(previousStateCode);
    } else {
      this.canAnswerAndAskForHelp = false;
    }
  }
}
